/* eslint-disable @typescript-eslint/naming-convention */

declare function importScripts(...urls: string[]): void;

const COMPRESSED_MAGIC: readonly number[] = [0x48, 0x53, 0x43, 0x42];
const DRACO_FORMAT: number = 2;
const QUANTIZED_FORMAT: number = 3;
const ADAPTIVE_QUANTIZED_FORMAT: number = 4;
const QUANTIZED_COLORS_RGB_FLAG: number = 1;
const QUANTIZED_COLLISIONS_FLAG: number = 2;
const QUANTIZED_COLORS_RGB565_FLAG: number = 8;
const DRACO_POINT_CLOUD_MESH_FLAG: number = 4;
const DRACO_POINT_CLOUD_RAW_FLAG: number = 8;
const ENVELOPE_HEADER_SIZE: number = 36;
const ENVELOPE_SIZE: number = 44;

export interface HsCompressedMeshGeometry {
  geometryId: string;
  canonicalVertexOrder?: boolean;
  vertices: number[];
  normals: number[];
  uvs: number[];
  indices: number[];
  canonicalIndices?: number[];
}

export interface HsCompressedPointCloudGeometry {
  geometryId: string;
  points: number[];
  colors: number[];
  collisions: number[];
}

interface DracoStatus {
  ok(): boolean;
  error_msg(): string;
}

interface DracoDecoderBuffer {
  Init(data: Int8Array, length: number): void;
}

interface DracoFloat32Array {
  size(): number;
  GetValue(index: number): number;
}

interface DracoInt32Array {
  GetValue(index: number): number;
}

interface DracoAttribute {
  num_components(): number;
}

interface DracoGeometry {
  num_points(): number;
  num_faces(): number;
}

interface DracoDecoder {
  GetAttributeId(geometry: DracoGeometry, attributeType: number): number;
  GetAttribute(geometry: DracoGeometry, attributeId: number): DracoAttribute;
  GetAttributeFloatForAllPoints(geometry: DracoGeometry, attribute: DracoAttribute, values: DracoFloat32Array): boolean;
  GetFaceFromMesh(mesh: DracoGeometry, faceIndex: number, face: DracoInt32Array): boolean;
  DecodeBufferToMesh(buffer: DracoDecoderBuffer, mesh: DracoGeometry): DracoStatus;
  DecodeBufferToPointCloud(buffer: DracoDecoderBuffer, pointCloud: DracoGeometry): DracoStatus;
}

interface DracoModule {
  Decoder: new () => DracoDecoder;
  DecoderBuffer: new () => DracoDecoderBuffer;
  Mesh: new () => DracoGeometry;
  PointCloud: new () => DracoGeometry;
  DracoFloat32Array: new () => DracoFloat32Array;
  DracoInt32Array: new () => DracoInt32Array;
  POSITION: number;
  NORMAL: number;
  TEX_COORD: number;
  GENERIC: number;
  destroy(value: object): void;
}

interface DracoGlobal {
  DracoDecoderModule?: (config: { locateFile: (file: string) => string }) => Promise<DracoModule>;
}

let dracoModulePromise: Promise<DracoModule> | null = null;
let decoderBaseUrl: string | null = null;
let dracoModuleFactory: DracoGlobal['DracoDecoderModule'] | undefined;

export interface HsDracoDecoderOptions {
  /**
   * Folder that serves `draco_decoder_nodejs.js` and `draco_decoder.wasm` (from the `draco3d` package).
   * Relative URLs resolve against the page or worker location. Default: `assets/decoders/`.
   */
  baseUrl?: string;
}

/** Points the decoder at the host's copy of the Draco files; call it before the first decode, in each worker too. */
export function hsConfigureDracoDecoder(options: HsDracoDecoderOptions): void {
  decoderBaseUrl = options.baseUrl ?? null;
  dracoModulePromise = null;
}

export function hsIsCompressedGeometryPayload(payload: ArrayBuffer): boolean {
  if (payload.byteLength < ENVELOPE_SIZE) {
    return false;
  }

  const bytes: Uint8Array = new Uint8Array(payload, 0, COMPRESSED_MAGIC.length);
  return COMPRESSED_MAGIC.every((value: number, index: number) => bytes[index] === value);
}

export async function hsPreloadDracoDecoder(): Promise<void> {
  await getDracoModule();
}

export async function hsDecodeCompressedMeshes(payload: ArrayBuffer): Promise<HsCompressedMeshGeometry[]> {
  return decodeCompressedMeshPayload(payload, false);
}

export async function hsDecodePhotogrammetryCompressedMeshes(
  payload: ArrayBuffer
): Promise<HsCompressedMeshGeometry[]> {
  return decodeCompressedMeshPayload(payload, true);
}

async function decodeCompressedMeshPayload(
  payload: ArrayBuffer,
  requireCanonicalMapping: boolean
): Promise<HsCompressedMeshGeometry[]> {
  const chunks: HsCompressedMeshGeometry[] = [];
  await forEachEnvelope(payload, async (view: DataView, offset: number, bodyOffset: number, bodyLength: number) => {
    const format: number = view.getUint8(offset + 5);
    const geometryId: string = readGeometryId(view, offset + 8);
    const body: Uint8Array = new Uint8Array(payload, bodyOffset, bodyLength);

    if (format === DRACO_FORMAT) {
      chunks.push(
        await decodeDracoMesh(
          body,
          geometryId,
          view.getUint8(offset + 36),
          view.getUint32(offset + 28, true),
          view.getUint32(offset + 32, true),
          requireCanonicalMapping
        )
      );
      return;
    }
    throw new Error(`Unsupported compressed mesh format: ${format}.`);
  });
  return chunks;
}

export async function hsDecodeCompressedPointClouds(payload: ArrayBuffer): Promise<HsCompressedPointCloudGeometry[]> {
  const chunks: HsCompressedPointCloudGeometry[] = [];
  await forEachEnvelope(payload, async (view: DataView, offset: number, bodyOffset: number, bodyLength: number) => {
    const format: number = view.getUint8(offset + 5);
    const geometryId: string = readGeometryId(view, offset + 8);
    const pointCount: number = view.getUint32(offset + 28, true);
    const flags: number = view.getUint8(offset + 36);
    const body: Uint8Array = new Uint8Array(payload, bodyOffset, bodyLength);

    if (format === QUANTIZED_FORMAT) {
      chunks.push(decodeQuantizedPointCloud(view, bodyOffset, bodyLength, geometryId, pointCount, flags, offset));
      return;
    }
    if (format === ADAPTIVE_QUANTIZED_FORMAT) {
      chunks.push(
        decodeAdaptiveQuantizedPointCloud(view, bodyOffset, bodyLength, geometryId, pointCount, flags, offset)
      );
      return;
    }
    if (format === DRACO_FORMAT) {
      try {
        chunks.push(await decodeDracoPointCloud(body, geometryId, pointCount, flags));
      } catch (error: unknown) {
        const errorMessage: string = error instanceof Error ? error.message : String(error);
        console.warn(`Skipping Draco point-cloud envelope ${geometryId}: ${errorMessage}`);
      }
      return;
    }
    throw new Error(`Unsupported compressed point-cloud format: ${format}.`);
  });
  return chunks;
}

async function decodeDracoMesh(
  body: Uint8Array,
  geometryId: string,
  flags: number,
  canonicalVertexCount: number,
  indexCount: number,
  requireCanonicalMapping: boolean
): Promise<HsCompressedMeshGeometry> {
  return withDracoModule((module: DracoModule) => {
    const hasLegacyCanonicalMapping: boolean = (flags & 2) !== 0;
    const hasCompactCanonicalMapping: boolean = (flags & 4) !== 0;
    if (
      (requireCanonicalMapping && !hasLegacyCanonicalMapping) ||
      (!requireCanonicalMapping && hasLegacyCanonicalMapping)
    ) {
      throw new Error(
        `Draco mesh ${geometryId} requires an ${requireCanonicalMapping ? 'photogrammetry v2' : 'standard v1'} variant.`
      );
    }
    let dracoBody: Uint8Array = body;
    let canonicalIndexData: DataView | undefined;
    if (hasLegacyCanonicalMapping) {
      if (body.byteLength < 4) {
        throw new Error(`Invalid Draco mesh index data for ${geometryId}.`);
      }
      const dracoLength: number = new DataView(body.buffer, body.byteOffset, body.byteLength).getUint32(0, true);
      const dracoOffset: number = 4;
      if (dracoOffset + dracoLength + indexCount * 4 !== body.byteLength) {
        throw new Error(`Invalid Draco mesh index data for ${geometryId}.`);
      }
      dracoBody = new Uint8Array(body.buffer, body.byteOffset + dracoOffset, dracoLength);
      canonicalIndexData = new DataView(body.buffer, body.byteOffset + dracoOffset + dracoLength, indexCount * 4);
    }
    const decoder: DracoDecoder = new module.Decoder();
    const buffer: DracoDecoderBuffer = new module.DecoderBuffer();
    const mesh: DracoGeometry = new module.Mesh();
    const bytes: Int8Array = new Int8Array(dracoBody.buffer, dracoBody.byteOffset, dracoBody.byteLength);
    buffer.Init(bytes, bytes.byteLength);

    try {
      const status: DracoStatus = decoder.DecodeBufferToMesh(buffer, mesh);
      if (!status.ok()) {
        throw new Error(`Draco mesh decode failed: ${status.error_msg()}`);
      }
      const vertices: number[] = readDracoAttribute(module, decoder, mesh, module.POSITION);
      const normals: number[] = readDracoAttribute(module, decoder, mesh, module.NORMAL);
      const uvs: number[] = readDracoAttribute(module, decoder, mesh, module.TEX_COORD);
      const indices: number[] = [];
      const face: DracoInt32Array = new module.DracoInt32Array();
      try {
        for (let index: number = 0; index < mesh.num_faces(); index++) {
          if (!decoder.GetFaceFromMesh(mesh, index, face)) {
            throw new Error('Draco mesh face decode failed.');
          }
          indices.push(face.GetValue(0), face.GetValue(1), face.GetValue(2));
        }
      } finally {
        module.destroy(face);
      }
      if (canonicalIndexData === undefined && !hasCompactCanonicalMapping) {
        return { geometryId, vertices, normals, uvs, indices };
      }
      const originalIds: number[] = readDracoAttribute(module, decoder, mesh, module.GENERIC);
      if (originalIds.length !== vertices.length / 3) {
        throw new Error(`Invalid Draco mesh canonical vertex mapping for ${geometryId}.`);
      }
      const currentIdsByOriginal: Map<number, number> = new Map();
      originalIds.forEach((id: number, current: number) => {
        if (!Number.isSafeInteger(id) || id < 0 || id >= canonicalVertexCount) {
          throw new Error(`Invalid Draco mesh canonical vertex id for ${geometryId}.`);
        }
        if (!currentIdsByOriginal.has(id)) {
          currentIdsByOriginal.set(id, current);
        }
      });
      if (hasCompactCanonicalMapping) {
        if (currentIdsByOriginal.size !== canonicalVertexCount) {
          throw new Error(`Incomplete Draco mesh canonical vertex mapping for ${geometryId}.`);
        }
        const canonicalVertices: number[] = new Array(canonicalVertexCount * 3);
        const canonicalNormals: number[] = new Array(canonicalVertexCount * 3);
        const canonicalUvs: number[] = new Array(uvs.length > 0 ? canonicalVertexCount * 2 : 0);
        currentIdsByOriginal.forEach((current: number, originalId: number) => {
          canonicalVertices.splice(originalId * 3, 3, ...vertices.slice(current * 3, current * 3 + 3));
          canonicalNormals.splice(originalId * 3, 3, ...normals.slice(current * 3, current * 3 + 3));
          if (uvs.length > 0) {
            canonicalUvs.splice(originalId * 2, 2, ...uvs.slice(current * 2, current * 2 + 2));
          }
        });
        const canonicalIndices: number[] = indices.map((index: number) => originalIds[index]);
        return {
          geometryId,
          canonicalVertexOrder: true,
          vertices: canonicalVertices,
          normals: canonicalNormals,
          uvs: canonicalUvs,
          indices: canonicalIndices,
        };
      }
      if (canonicalIndexData === undefined) {
        throw new Error(`Invalid Draco mesh index data for ${geometryId}.`);
      }
      const canonicalIndices: number[] = [];
      for (let index: number = 0; index < indexCount; index++) {
        const currentId: number | undefined = currentIdsByOriginal.get(canonicalIndexData.getInt32(index * 4, true));
        if (currentId === undefined) {
          throw new Error(`Invalid Draco mesh canonical triangle for ${geometryId}.`);
        }
        canonicalIndices.push(currentId);
      }
      return { geometryId, vertices, normals, uvs, indices, canonicalIndices };
    } finally {
      module.destroy(mesh);
      module.destroy(buffer);
      module.destroy(decoder);
    }
  });
}

async function decodeDracoPointCloud(
  body: Uint8Array,
  geometryId: string,
  pointCount: number,
  flags: number
): Promise<HsCompressedPointCloudGeometry> {
  const view: DataView = new DataView(body.buffer, body.byteOffset, body.byteLength);
  if (body.byteLength < 12) {
    throw new Error('Invalid Draco point-cloud body.');
  }

  const dracoLength: number = view.getUint32(0, true);
  const colorCount: number = view.getUint32(4, true);
  const collisionCount: number = view.getUint32(8, true);
  if ((flags & DRACO_POINT_CLOUD_RAW_FLAG) !== 0) {
    return decodeRawDracoPointCloud(view, body, geometryId, pointCount, colorCount, collisionCount, flags);
  }

  const dracoOffset: number = 12;
  const colorsOffset: number = dracoOffset + dracoLength;
  const collisionsOffset: number = colorsOffset + colorCount * 4;
  if (collisionsOffset + collisionCount > body.byteLength) {
    throw new Error('Invalid Draco point-cloud buffer lengths.');
  }

  const useMeshDecoder: boolean = (flags & DRACO_POINT_CLOUD_MESH_FLAG) !== 0;
  const decoded: { points: number[]; pointIndices: number[] } = await withDracoModule((module: DracoModule) => {
    const decoder: DracoDecoder = new module.Decoder();
    const buffer: DracoDecoderBuffer = new module.DecoderBuffer();
    const geometry: DracoGeometry = useMeshDecoder ? new module.Mesh() : new module.PointCloud();
    const dracoBytes: Int8Array = new Int8Array(body.buffer, body.byteOffset + dracoOffset, dracoLength);
    buffer.Init(dracoBytes, dracoBytes.byteLength);
    try {
      const status: DracoStatus = useMeshDecoder
        ? decoder.DecodeBufferToMesh(buffer, geometry)
        : decoder.DecodeBufferToPointCloud(buffer, geometry);
      if (!status.ok()) {
        throw new Error(`Draco point-cloud decode failed: ${status.error_msg()}`);
      }
      return {
        points: readDracoAttribute(module, decoder, geometry, module.POSITION),
        pointIndices:
          useMeshDecoder && module.GENERIC !== undefined
            ? readDracoAttribute(module, decoder, geometry, module.GENERIC)
            : [],
      };
    } finally {
      module.destroy(geometry);
      module.destroy(buffer);
      module.destroy(decoder);
    }
  });

  const sourceColors: number[] =
    (flags & 1) !== 0 ? readInt32Values(view, colorsOffset, colorCount) : new Array<number>(pointCount).fill(0);
  const sourceCollisions: number[] =
    (flags & 2) !== 0
      ? Array.from(new Uint8Array(body.buffer, body.byteOffset + collisionsOffset, collisionCount))
      : new Array<number>(pointCount).fill(0);
  const pointCountFromPayload: number = decoded.points.length / 3;
  const reordered: { points: number[]; colors: number[]; collisions: number[] } = useMeshDecoder
    ? reorderPointCloudAttributes(decoded.points, decoded.pointIndices, sourceColors, sourceCollisions)
    : {
        points: decoded.points,
        colors:
          sourceColors.length === pointCountFromPayload
            ? sourceColors
            : new Array<number>(pointCountFromPayload).fill(0),
        collisions:
          sourceCollisions.length === pointCountFromPayload
            ? sourceCollisions
            : new Array<number>(pointCountFromPayload).fill(0),
      };

  return {
    geometryId,
    points: reordered.points,
    colors: reordered.colors,
    collisions: reordered.collisions,
  };
}

/**
 * Point clouds sent with `format=quantized`: 16-bit positions inside the block box, RGB colors sharing one
 * high byte (or int32 colors) and collision flags only when a point has one. Needs no Draco module.
 */
function decodeQuantizedPointCloud(
  view: DataView,
  bodyOffset: number,
  bodyLength: number,
  geometryId: string,
  pointCount: number,
  flags: number,
  envelopeOffset: number
): HsCompressedPointCloudGeometry {
  const rgb: boolean = (flags & QUANTIZED_COLORS_RGB_FLAG) !== 0;
  const hasCollisions: boolean = (flags & QUANTIZED_COLLISIONS_FLAG) !== 0;
  const expected: number = 24 + pointCount * (6 + (rgb ? 3 : 4) + (hasCollisions ? 1 : 0));
  if (expected !== bodyLength) {
    throw new Error(`Invalid quantized point-cloud body length: ${bodyLength}, expected ${expected}.`);
  }

  const min: number[] = [0, 1, 2].map((axis: number) => view.getFloat32(bodyOffset + axis * 4, true));
  const step: number[] = [0, 1, 2].map((axis: number) => view.getFloat32(bodyOffset + 12 + axis * 4, true));
  let cursor: number = bodyOffset + 24;
  const points: number[] = new Array<number>(pointCount * 3);
  for (let index: number = 0; index < pointCount * 3; index++) {
    const axis: number = index % 3;
    points[index] = min[axis] + view.getUint16(cursor + index * 2, true) * step[axis];
  }
  cursor += pointCount * 6;

  const colors: number[] = new Array<number>(pointCount);
  if (rgb) {
    // The high byte shared by every point travels once, in the envelope header after the flags.
    const highByte: number = view.getUint8(envelopeOffset + 37) << 24;
    for (let index: number = 0; index < pointCount; index++) {
      const at: number = cursor + index * 3;
      colors[index] = highByte | (view.getUint8(at + 2) << 16) | (view.getUint8(at + 1) << 8) | view.getUint8(at);
    }
    cursor += pointCount * 3;
  } else {
    for (let index: number = 0; index < pointCount; index++) {
      colors[index] = view.getInt32(cursor + index * 4, true);
    }
    cursor += pointCount * 4;
  }

  const collisions: number[] = new Array<number>(pointCount).fill(0);
  if (hasCollisions) {
    for (let index: number = 0; index < pointCount; index++) {
      collisions[index] = view.getUint8(cursor + index);
    }
  }

  return { geometryId, points, colors, collisions };
}

/**
 * Point clouds sent with `format=quantized2`: as many bits per axis as the block's spacing needs (header byte
 * after the high byte), one uint32 per point (x | y << bits | z << 2 bits) when three axes fit 32 bits, else
 * uint16 x3, and RGB565 colors where they survive the round trip within 4 per channel.
 */
function decodeAdaptiveQuantizedPointCloud(
  view: DataView,
  bodyOffset: number,
  bodyLength: number,
  geometryId: string,
  pointCount: number,
  flags: number,
  envelopeOffset: number
): HsCompressedPointCloudGeometry {
  const bits: number = view.getUint8(envelopeOffset + 38);
  const packed: boolean = bits * 3 <= 32;
  const rgb565: boolean = (flags & QUANTIZED_COLORS_RGB565_FLAG) !== 0;
  const rgb: boolean = (flags & QUANTIZED_COLORS_RGB_FLAG) !== 0;
  const colorBytes: number = rgb565 ? 2 : rgb ? 3 : 4;
  const hasCollisions: boolean = (flags & QUANTIZED_COLLISIONS_FLAG) !== 0;
  const expected: number = 24 + pointCount * ((packed ? 4 : 6) + colorBytes + (hasCollisions ? 1 : 0));
  if (bits < 1 || bits > 16 || expected !== bodyLength) {
    throw new Error(`Invalid quantized2 point-cloud body: ${bits} bits, length ${bodyLength}, expected ${expected}.`);
  }

  const min: number[] = [0, 1, 2].map((axis: number) => view.getFloat32(bodyOffset + axis * 4, true));
  const step: number[] = [0, 1, 2].map((axis: number) => view.getFloat32(bodyOffset + 12 + axis * 4, true));
  let cursor: number = bodyOffset + 24;
  const points: number[] = new Array<number>(pointCount * 3);
  if (packed) {
    const mask: number = (1 << bits) - 1;
    for (let index: number = 0; index < pointCount; index++) {
      const word: number = view.getUint32(cursor + index * 4, true);
      points[index * 3] = min[0] + (word & mask) * step[0];
      points[index * 3 + 1] = min[1] + ((word >>> bits) & mask) * step[1];
      points[index * 3 + 2] = min[2] + ((word >>> (bits * 2)) & mask) * step[2];
    }
    cursor += pointCount * 4;
  } else {
    for (let index: number = 0; index < pointCount * 3; index++) {
      points[index] = min[index % 3] + view.getUint16(cursor + index * 2, true) * step[index % 3];
    }
    cursor += pointCount * 6;
  }

  const colors: number[] = new Array<number>(pointCount);
  const highByte: number = view.getUint8(envelopeOffset + 37) << 24;
  for (let index: number = 0; index < pointCount; index++) {
    if (rgb565) {
      const value: number = view.getUint16(cursor + index * 2, true);
      colors[index] =
        highByte |
        (Math.round(((value >>> 11) * 255) / 31) << 16) |
        (Math.round((((value >>> 5) & 0x3f) * 255) / 63) << 8) |
        Math.round(((value & 0x1f) * 255) / 31);
    } else if (rgb) {
      const at: number = cursor + index * 3;
      colors[index] = highByte | (view.getUint8(at + 2) << 16) | (view.getUint8(at + 1) << 8) | view.getUint8(at);
    } else {
      colors[index] = view.getInt32(cursor + index * 4, true);
    }
  }
  cursor += pointCount * colorBytes;

  const collisions: number[] = new Array<number>(pointCount).fill(0);
  if (hasCollisions) {
    for (let index: number = 0; index < pointCount; index++) {
      collisions[index] = view.getUint8(cursor + index);
    }
  }

  return { geometryId, points, colors, collisions };
}

function decodeRawDracoPointCloud(
  view: DataView,
  body: Uint8Array,
  geometryId: string,
  pointCount: number,
  colorCount: number,
  collisionCount: number,
  flags: number
): HsCompressedPointCloudGeometry {
  const pointsOffset: number = 12;
  const colorsOffset: number = pointsOffset + pointCount * 12;
  const collisionsOffset: number = colorsOffset + colorCount * 4;
  if (collisionsOffset + collisionCount > body.byteLength) {
    throw new Error('Invalid raw Draco point-cloud buffer lengths.');
  }

  const points: number[] = [];
  for (let index: number = 0; index < pointCount * 3; index++) {
    points.push(view.getFloat32(pointsOffset + index * 4, true));
  }
  const colors: number[] =
    (flags & 1) !== 0 ? readInt32Values(view, colorsOffset, colorCount) : new Array<number>(pointCount).fill(0);
  const collisions: number[] =
    (flags & 2) !== 0
      ? Array.from(new Uint8Array(body.buffer, body.byteOffset + collisionsOffset, collisionCount))
      : new Array<number>(pointCount).fill(0);

  return {
    geometryId,
    points,
    colors: colors.length === pointCount ? colors : new Array<number>(pointCount).fill(0),
    collisions: collisions.length === pointCount ? collisions : new Array<number>(pointCount).fill(0),
  };
}

function reorderPointCloudAttributes(
  points: number[],
  pointIndices: number[],
  sourceColors: number[],
  sourceCollisions: number[]
): { points: number[]; colors: number[]; collisions: number[] } {
  if (pointIndices.length !== points.length / 3) {
    throw new Error('Draco point-cloud index attribute length does not match positions.');
  }

  const reorderedPoints: number[] = [];
  const colors: number[] = [];
  const collisions: number[] = [];
  const seen: Set<number> = new Set<number>();
  for (let index: number = 0; index < pointIndices.length; index++) {
    const sourceIndex: number = pointIndices[index];
    if (!Number.isInteger(sourceIndex) || sourceIndex < 0 || sourceIndex >= sourceColors.length) {
      throw new Error('Draco point-cloud index attribute is invalid.');
    }
    if (seen.has(sourceIndex)) {
      continue;
    }
    seen.add(sourceIndex);
    reorderedPoints.push(points[index * 3], points[index * 3 + 1], points[index * 3 + 2]);
    colors.push(sourceColors[sourceIndex] ?? 0);
    collisions.push(sourceCollisions[sourceIndex] ?? 0);
  }
  if (seen.size !== sourceColors.length) {
    throw new Error('Draco point-cloud index attribute is incomplete.');
  }
  return { points: reorderedPoints, colors, collisions };
}

function readInt32Values(view: DataView, offset: number, count: number): number[] {
  const values: number[] = new Array<number>(count);
  for (let index: number = 0; index < count; index++) {
    values[index] = view.getInt32(offset + index * 4, true);
  }
  return values;
}

function readDracoAttribute(
  module: DracoModule,
  decoder: DracoDecoder,
  geometry: DracoGeometry,
  attributeType: number
): number[] {
  const attributeId: number = decoder.GetAttributeId(geometry, attributeType);
  if (attributeId < 0) {
    return [];
  }

  const attribute: DracoAttribute = decoder.GetAttribute(geometry, attributeId);
  const values: DracoFloat32Array = new module.DracoFloat32Array();
  if (!decoder.GetAttributeFloatForAllPoints(geometry, attribute, values)) {
    module.destroy(values);
    throw new Error('Draco attribute decode failed.');
  }

  try {
    const result: number[] = [];
    for (let index: number = 0; index < values.size(); index++) {
      result.push(values.GetValue(index));
    }
    return result;
  } finally {
    module.destroy(values);
  }
}

async function forEachEnvelope(
  payload: ArrayBuffer,
  callback: (view: DataView, offset: number, bodyOffset: number, bodyLength: number) => Promise<void> | void
): Promise<void> {
  if (!hsIsCompressedGeometryPayload(payload)) {
    throw new Error('Invalid compressed geometry payload magic.');
  }

  const view: DataView = new DataView(payload);
  let offset: number = 0;
  while (offset < payload.byteLength) {
    if (offset + ENVELOPE_SIZE > payload.byteLength) {
      throw new Error('Invalid compressed geometry envelope.');
    }
    const headerSize: number = view.getUint16(offset + 6, true);
    const bodyLength: number = view.getUint32(offset + 40, true);
    const bodyOffset: number = offset + 8 + headerSize;
    const chunkEnd: number = bodyOffset + bodyLength;
    if (view.getUint32(offset, true) !== 0x42435348 || view.getUint8(offset + 4) !== 1) {
      throw new Error('Unsupported compressed geometry envelope.');
    }
    if (headerSize !== ENVELOPE_HEADER_SIZE || chunkEnd > payload.byteLength) {
      throw new Error('Compressed geometry envelope exceeds payload bounds.');
    }
    await callback(view, offset, bodyOffset, bodyLength);
    offset = chunkEnd;
  }
}

function readGeometryId(view: DataView, offset: number): string {
  const bytes: string[] = [];
  for (let index: number = 0; index < 16; index++) {
    bytes.push(
      view
        .getUint8(offset + index)
        .toString(16)
        .padStart(2, '0')
    );
  }
  return [
    `${bytes[0]}${bytes[1]}${bytes[2]}${bytes[3]}`,
    `${bytes[4]}${bytes[5]}`,
    `${bytes[6]}${bytes[7]}`,
    `${bytes[8]}${bytes[9]}`,
    `${bytes[10]}${bytes[11]}${bytes[12]}${bytes[13]}${bytes[14]}${bytes[15]}`,
  ].join('-');
}

async function withDracoModule<T>(callback: (module: DracoModule) => T): Promise<T> {
  const module: DracoModule = await getDracoModule();
  return callback(module);
}

async function getDracoModule(): Promise<DracoModule> {
  const currentFactory: DracoGlobal['DracoDecoderModule'] = (globalThis as unknown as DracoGlobal).DracoDecoderModule;
  if (dracoModulePromise === null || (currentFactory !== undefined && currentFactory !== dracoModuleFactory)) {
    dracoModuleFactory = currentFactory;
    dracoModulePromise = loadDracoModule();
  }
  return dracoModulePromise;
}

async function loadDracoModule(): Promise<DracoModule> {
  const globalObject: DracoGlobal = globalThis as unknown as DracoGlobal;
  if (globalObject.DracoDecoderModule === undefined) {
    const scriptUrl: string = getDecoderAssetUrl('draco_decoder_nodejs.js');
    if (typeof document !== 'undefined') {
      await new Promise<void>((resolve: () => void, reject: (reason?: unknown) => void) => {
        const script: HTMLScriptElement = document.createElement('script');
        script.src = scriptUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Unable to load Draco decoder script.'));
        document.head.appendChild(script);
      });
    } else if (typeof importScripts === 'function') {
      importScripts(scriptUrl);
    }
  }

  const factory: ((config: { locateFile: (file: string) => string }) => Promise<DracoModule>) | undefined = (
    globalThis as unknown as DracoGlobal
  ).DracoDecoderModule;
  if (factory === undefined) {
    throw new Error('Draco decoder module is unavailable.');
  }
  return factory({ locateFile: (file: string) => getDecoderAssetUrl(file) });
}

function getDecoderAssetUrl(file: string): string {
  const base: string =
    typeof document !== 'undefined' ? document.baseURI : typeof self !== 'undefined' ? self.location.href : '';
  const folder: string = decoderBaseUrl ?? 'assets/decoders/';
  return new URL(file, base === '' ? new URL(folder) : new URL(folder, base)).toString();
}
